import React, { useState, useEffect } from "react";
import {
  getDatabase,
  ref,
  onValue,
  set,
  remove,
  push,
} from "firebase/database";
import firebaseApp from "../firebase";

const liveDb = getDatabase(firebaseApp);

const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
};

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [newNews, setNewNews] = useState({
    title: "",
    date: "",
    description: "",
    link: "",
  });
  const [editNews, setEditNews] = useState(null);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsRef = ref(liveDb, "news");
        onValue(newsRef, (snapshot) => {
          const newsData = snapshot.val();
          if (newsData) {
            const newsArray = Object.keys(newsData)
              .map((key) => ({
                id: key,
                ...newsData[key],
              }))
              .sort((a, b) => new Date(b.date) - new Date(a.date));
            setNews(newsArray);
          }
        });
      } catch (error) {
        console.error("Error fetching news:", error);
      }
    };

    fetchNews();
  }, []);

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!newNews.date || !newNews.description || !newNews.title) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      const newsRef = ref(liveDb, "news");
      const newNewsRef = push(newsRef);
      await set(newNewsRef, newNews);
      alert("News added successfully.");
      setNewNews({ title: "", date: "", description: "", link: "" });
    } catch (error) {
      console.error("Error adding news:", error);
      alert("Failed to add news.");
    }
  };

  const handleEditNews = (newsItem) => {
    setEditNews(newsItem);
    setIsNewsModalOpen(true);
  };

  const handleEditNewsSubmit = async (e) => {
    e.preventDefault();
    if (!editNews.date || !editNews.description || !editNews.title) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      const newsRef = ref(liveDb, `news/${editNews.id}`);
      await set(newsRef, {
        title: editNews.title,
        date: editNews.date,
        description: editNews.description,
        link: editNews.link,
      });
      alert("News updated successfully.");
      setIsNewsModalOpen(false);
      setEditNews(null);
    } catch (error) {
      console.error("Error updating news:", error);
      alert("Failed to update news.");
    }
  };

  const handleDeleteNews = async (id) => {
    try {
      const newsRef = ref(liveDb, `news/${id}`);
      await remove(newsRef);
      alert("News deleted successfully.");
    } catch (error) {
      console.error("Error deleting news:", error);
      alert("Failed to delete news.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        News Management
      </h1>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Add News</h2>
        <form onSubmit={handleNewsSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              placeholder="News title"
              value={newNews.title}
              onChange={(e) =>
                setNewNews({ ...newNews, title: e.target.value })
              }
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
            <label className="text-sm font-medium text-gray-700">Date</label>
            <input
              type="text"
              placeholder="YYYY-MM-DD"
              value={newNews.date}
              onChange={(e) => setNewNews({ ...newNews, date: e.target.value })}
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-start">
            <label className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              placeholder="News description"
              value={newNews.description}
              onChange={(e) =>
                setNewNews({ ...newNews, description: e.target.value })
              }
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              rows="4"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
            <label className="text-sm font-medium text-gray-700">
              Link (optional)
            </label>
            <input
              type="text"
              placeholder="https://example.com"
              value={newNews.link}
              onChange={(e) => setNewNews({ ...newNews, link: e.target.value })}
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200"
            >
              Add News
            </button>
          </div>
        </form>
      </section>
      <h3 className="text-lg font-semibold text-gray-700 mb-4">
        Existing News
      </h3>
      {news.length === 0 ? (
        <p className="text-gray-600">No news items found.</p>
      ) : (
        <div className="space-y-4">
          {news.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-md p-4 bg-gray-50 hover:bg-gray-100 transition duration-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    <strong>Title:</strong> {item.title}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Date:</strong> {item.date}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Description:</strong>{" "}
                    {truncateText(item.description, 100)}
                  </p>
                  {item.link && (
                    <p className="text-sm text-gray-600">
                      <strong>Link:</strong>{" "}
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {truncateText(item.link, 50)}
                      </a>
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleEditNews(item)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteNews(item.id)}
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {isNewsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              Edit News
            </h2>
            <form onSubmit={handleEditNewsSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
                <label className="text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  value={editNews?.title || ""}
                  onChange={(e) =>
                    setEditNews({ ...editNews, title: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
                <label className="text-sm font-medium text-gray-700">
                  Date
                </label>
                <input
                  type="text"
                  value={editNews?.date || ""}
                  onChange={(e) =>
                    setEditNews({ ...editNews, date: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-start">
                <label className="text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  value={editNews?.description || ""}
                  onChange={(e) =>
                    setEditNews({ ...editNews, description: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  rows="4"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
                <label className="text-sm font-medium text-gray-700">
                  Link (optional)
                </label>
                <input
                  type="text"
                  value={editNews?.link || ""}
                  onChange={(e) =>
                    setEditNews({ ...editNews, link: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setIsNewsModalOpen(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
